<template>
  <div class="lobby">
    <div class="lobby__container">
      <!-- Preview panel -->
      <div class="lobby__preview-panel">
        <div class="lobby__video-wrap">
          <!-- Camera preview -->
          <video
            v-show="!localVideoMuted && !permissionError && cameraReady"
            ref="previewVideo"
            class="lobby__preview-video"
            autoplay
            playsinline
            muted
          />

          <!-- Avatar fallback -->
          <div
            v-if="localVideoMuted || !cameraReady"
            class="lobby__preview-avatar"
          >
            <div class="lobby__avatar-circle">{{ initials }}</div>
          </div>

          <!-- Permission error overlay -->
          <div v-if="permissionError" class="lobby__permission-error">
            <v-icon size="40" color="error">mdi-camera-off</v-icon>
            <p class="mt-2">{{ permissionError }}</p>
          </div>

          <!-- Loading overlay -->
          <div v-if="loadingDevices && !permissionError" class="lobby__loading">
            <v-progress-circular indeterminate color="white" size="32" />
          </div>
        </div>

        <!-- Preview controls -->
        <div class="lobby__preview-controls">
          <v-tooltip :text="localAudioMuted ? 'Unmute' : 'Mute mic'" location="top">
            <template #activator="{ props }">
              <button
                v-bind="props"
                class="prev-btn"
                :class="{ 'prev-btn--off': localAudioMuted }"
                @click="toggleAudio"
              >
                <v-icon size="20">{{ localAudioMuted ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
              </button>
            </template>
          </v-tooltip>

          <v-tooltip :text="localVideoMuted ? 'Start camera' : 'Stop camera'" location="top">
            <template #activator="{ props }">
              <button
                v-bind="props"
                class="prev-btn"
                :class="{ 'prev-btn--off': localVideoMuted }"
                @click="toggleVideo"
              >
                <v-icon size="20">{{ localVideoMuted ? 'mdi-video-off' : 'mdi-video' }}</v-icon>
              </button>
            </template>
          </v-tooltip>
        </div>
      </div>

      <!-- Join panel -->
      <div class="lobby__join-panel">
        <div class="lobby__brand">
          <v-icon size="28"  class="mr-2">mdi-video-box</v-icon>
          <span class="lobby__brand-name">DSS Meet</span>
        </div>

        <h2 class="lobby__title">Ready to join?</h2>
        <p class="lobby__subtitle">
          <v-icon size="16" class="mr-1">mdi-door</v-icon>
          <strong>{{ roomId }}</strong>
        </p>

        <!-- Role selector — non-login users only -->
        <v-select
          v-if="!isLoggedIn"
          v-model="selectedRole"
          :items="[PARTICIPANT_ROLES.HOSPITAL_WORKER, PARTICIPANT_ROLES.PATIENT]"
          label="I am a..."
          variant="outlined"
          density="compact"
          color="blue-lighten-2"
          class="mt-4"
          hide-details
          style="background-color: white;"
          @update:model-value="onRoleSelected"
        />

        <!-- Name input -->
          <v-text-field
            v-model="participantName"
            style="background-color: white;"
            label="Your name"
            variant="outlined"
            density="compact"
            color="blue-lighten-2"
            class="mt-4"
            hide-details
            :readonly="isLoggedIn"
            @keyup.enter="join"
          />

        <!-- Patient contact fields -->
        <template v-if="!isLoggedIn && selectedRole === PARTICIPANT_ROLES.PATIENT">
          <v-text-field
            v-model="patientPhone"
            label="Phone number (optional)"
            variant="outlined"
            density="compact"
            color="blue-lighten-2"
            class="mt-3"
            hide-details
            style="background-color: white;"
            prepend-inner-icon="mdi-phone"
          />
          <v-text-field
            v-model="patientEmail"
            label="Email (optional)"
            variant="outlined"
            density="compact"
            color="blue-lighten-2"
            class="mt-3"
            hide-details
            style="background-color: white;"
            prepend-inner-icon="mdi-email"
          />
        </template>

        <!-- Device selectors -->
        <div v-if="audioInputs.length > 0" class="mt-3">
          <v-select
            v-model="selectedAudio"
            style="background-color: white;"
            :items="audioInputs"
            item-title="label"
            item-value="deviceId"
            label="Microphone"
            variant="outlined"
            density="compact"
            color="blue-lighten-2"
            hide-details
            prepend-inner-icon="mdi-microphone"
            @update:model-value="switchAudioDevice"
          />
        </div>

        <div v-if="videoInputs.length > 0" class="mt-3">
          <v-select
            v-model="selectedVideo"
            :items="videoInputs"
            item-title="label"
            item-value="deviceId"
            label="Camera"
            variant="outlined"
            density="compact"
            color="blue-lighten-2"
            hide-details
            prepend-inner-icon="mdi-camera"
            @update:model-value="switchVideoDevice"
          />
        </div>

        <div v-if="supportsSpeakerSelection" class="mt-3">
          <v-select
            v-model="selectedSpeaker"
            style="background-color: white;"
            :items="audioOutputs"
            item-title="label"
            item-value="deviceId"
            label="Speaker"
            variant="outlined"
            density="compact"
            color="blue-lighten-2"
            hide-details
            prepend-inner-icon="mdi-volume-high"
          />
        </div>

        <!-- Join button -->
        <v-btn
          block
          color="blue"
          size="large"
          class="mt-5"
          :disabled="!participantName.trim() || joining"
          :loading="joining"
          @click="join"
        >
          Join now
        </v-btn>

        <p v-if="joinError" class="lobby__error mt-2">{{ joinError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  PARTICIPANT_ROLES,
  DEVICE_KINDS,
  MEDIA_ERROR_NAMES,
  SESSION_STORAGE_KEYS,
} from '../../constants/video';
import speakerDeviceMixin from '../../mixins/speakerDeviceMixin';

export default {
  name: 'LobbyScreen',

  mixins: [speakerDeviceMixin],

  props: {
    roomId: { type: String, default: '' },
    defaultName: { type: String, default: '' },
    isLoggedIn: { type: Boolean, default: false },
  },

  emits: ['join'],

  data() {
    return {
      participantName: this.defaultName || '',
      localAudioMuted: false,
      localVideoMuted: false,
      previewStream: null,
      audioInputs: [],
      videoInputs: [],
      selectedAudio: null,
      selectedVideo: null,
      selectedSpeaker: null,
      loadingDevices: true,
      cameraReady: false,
      permissionError: null,
      joining: false,
      joinError: null,
      // Non-login role selection
      PARTICIPANT_ROLES,  // exposed for template role comparisons
      selectedRole: null,
      patientPhone: '',
      patientEmail: '',
    };
  },

  computed: {
    initials() {
      return (this.participantName || 'G')
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('');
    },
  },

  async mounted() {
    await this.initDevices();
  },

  beforeUnmount() {
    this.stopPreview();
  },

  methods: {
    async initDevices() {
      this.loadingDevices = true;
      this.permissionError = null;
      try {
        this.previewStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        this.$refs.previewVideo.srcObject = this.previewStream;
        this.cameraReady = true;

        const devices = await navigator.mediaDevices.enumerateDevices();
        this.audioInputs = devices
          .filter((d) => d.kind === DEVICE_KINDS.AUDIO_INPUT)
          .map((d) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 6)}` }));
        this.videoInputs = devices
          .filter((d) => d.kind === DEVICE_KINDS.VIDEO_INPUT)
          .map((d) => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 6)}` }));
        this.setAudioOutputsFromDevices(devices);

        if (this.audioInputs.length) this.selectedAudio = this.audioInputs[0].deviceId;
        if (this.videoInputs.length) this.selectedVideo = this.videoInputs[0].deviceId;
        if (this.audioOutputs.length) this.selectedSpeaker = this.audioOutputs[0].deviceId;
      } catch (err) {
        if (err.name === MEDIA_ERROR_NAMES.NOT_ALLOWED || err.name === MEDIA_ERROR_NAMES.PERMISSION_DENIED) {
          this.permissionError = 'Camera/microphone permission denied. Please allow access in your browser settings.';
        } else if (err.name === MEDIA_ERROR_NAMES.NOT_FOUND) {
          this.permissionError = 'No camera or microphone found on this device.';
        } else {
          this.permissionError = 'Could not access camera/microphone.';
        }
      } finally {
        this.loadingDevices = false;
      }
    },

    toggleAudio() {
      if (!this.previewStream) return;
      this.localAudioMuted = !this.localAudioMuted;
      this.previewStream.getAudioTracks().forEach((t) => {
        t.enabled = !this.localAudioMuted;
      });
    },

    toggleVideo() {
      if (!this.previewStream) return;
      this.localVideoMuted = !this.localVideoMuted;
      this.previewStream.getVideoTracks().forEach((t) => {
        t.enabled = !this.localVideoMuted;
      });
    },

    async switchAudioDevice(deviceId) {
      if (!this.previewStream) return;
      this.previewStream.getAudioTracks().forEach((t) => t.stop());
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
          video: false,
        });
        newStream.getAudioTracks().forEach((t) => this.previewStream.addTrack(t));
      } catch (e) {
        console.warn('Could not switch audio device', e);
      }
    },

    async switchVideoDevice(deviceId) {
      if (!this.previewStream) return;
      this.previewStream.getVideoTracks().forEach((t) => t.stop());
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
          audio: false,
        });
        const newTrack = newStream.getVideoTracks()[0];
        if (newTrack) {
          this.previewStream.addTrack(newTrack);
          this.$refs.previewVideo.srcObject = this.previewStream;
        }
      } catch (e) {
        console.warn('Could not switch video device', e);
      }
    },

    stopPreview() {
      if (this.previewStream) {
        this.previewStream.getTracks().forEach((t) => t.stop());
        this.previewStream = null;
      }
    },

    onRoleSelected(role) {
      if (role === PARTICIPANT_ROLES.HOSPITAL_WORKER) {
        // Store the intended room and redirect to login; router restores after auth
        sessionStorage.setItem(SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT, `/#/video-call/${this.roomId}`);
        window.location.href = '/#/';
      }
    },

    join() {
      if (!this.participantName.trim() || this.joining) return;
      if (!this.isLoggedIn && !this.selectedRole) {
        this.joinError = 'Please select your role before joining.';
        return;
      }
      this.joining = true;
      this.joinError = null;

      // Stop preview tracks so camera LED turns off before LiveKit re-acquires them
      this.stopPreview();

      this.$emit('join', {
        participantName: this.participantName.trim(),
        audioDeviceId: this.selectedAudio,
        videoDeviceId: this.selectedVideo,
        speakerDeviceId: this.selectedSpeaker,
        audioMuted: this.localAudioMuted,
        videoMuted: this.localVideoMuted,
        role: this.selectedRole,
        patientPhone: this.patientPhone,
        patientEmail: this.patientEmail,
      });
    },
  },
};
</script>

<style scoped>
.lobby {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5% 0 0 5%;
}

.lobby__container {
  display: flex;
  gap: 40px;
  max-width: 900px;
  width: 100%;
  align-items: center;
}

/* Preview panel */
.lobby__preview-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.lobby__video-wrap {
  position: relative;
  width: 100%;
  max-width: 480px;
  aspect-ratio: 16 / 9;
  background: #2a2a2a;
  border-radius: 12px;
  overflow: hidden;
}

.lobby__preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
}

.lobby__preview-avatar {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2a2a2a;
}

.lobby__avatar-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #5c6bc0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
}

.lobby__permission-error {
  position: absolute;
  inset: 0;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  color: #bdbdbd;
  font-size: 0.8rem;
}

.lobby__loading {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.lobby__preview-controls {
  display: flex;
  gap: 12px;
}

.prev-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #333;
  color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}

.prev-btn:hover {
  background: #444;
}

.prev-btn--off {
  background: #b00020;
  color: #fff;
}

.prev-btn--off:hover {
  background: #c62828;
}

/* Join panel */
.lobby__join-panel {
  flex: 0 0 320px;
  background: #ffffff;
  border-radius: 16px;
  padding: 28px 24px;
}

.lobby__brand {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.lobby__brand-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #000000;
}

.lobby__title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #000000;
  margin: 0 0 4px;
}

.lobby__subtitle {
  font-size: 0.85rem;
  color: #110c0c;
  display: flex;
  align-items: center;
  margin: 0;
}

.lobby__error {
  color: #ef5350;
  font-size: 0.8rem;
  text-align: center;
}

/* Responsive */
@media (max-width: 640px) {
  .lobby__container {
    flex-direction: column;
  }
  .lobby__join-panel {
    flex: 1;
    width: 100%;
  }
}
</style>
